/**
 * @file     emailConsent.ts
 * @purpose  The optional email-updates consent: its wording, version, the
 *           identity line every consent request shows, and the payload a form
 *           sends. The server looks the text up by version and locale, so the
 *           ledger records what the person saw, not what a client claims.
 */

/**
 * Bump whenever any wording below changes, and keep the old text resolvable in
 * src/services/consent/consentText.ts so earlier grants still prove their text.
 */
export const EMAIL_CONSENT_VERSION = '2026-10-11-v1';

/**
 * English source. `identity` is shown under the boxes whenever either box
 * shows: SOR/2012-36 s.4 asks a consent request to name the sender, give a
 * mailing address and a contact, and say consent can be withdrawn.
 * `{{mailingAddress}}` is brandConfig.mailingAddress and is never translated.
 */
export const EMAIL_CONSENT_COPY = {
  emailOptIn: 'Email me market updates, new listings, and news about Relta tools and services.',
  identity:
    'From Ray Rasouli, Real Broker B.C. Ltd., {{mailingAddress}} · ray@relta.ca. You can withdraw consent any time by unsubscribing or replying STOP.',
  settingsLabel: 'Email me market updates, new listings, and news about Relta tools and services.',
  settingsHelper: 'You can turn this off any time.',
} as const;

/**
 * The buttons that turn on listing alerts. Pressing one is the person asking
 * for those emails, so the ledger records the button's words as the text shown.
 */
export const ALERT_SETUP_COPY = {
  savedSearch: 'Save search',
  alertSignup: 'Get alerts',
  propertyFinder: 'Get new homes like these by email. Save this search',
  savedSearchResume: 'Resume',
  appSaveSearch: 'Save and turn on alerts',
  appTurnOnAlerts: 'Turn on alerts',
} as const;

export type AlertSetupSurface = keyof typeof ALERT_SETUP_COPY;

/** Bump with any change to ALERT_SETUP_COPY. */
export const ALERT_SETUP_VERSION = '2026-10-11-v1';

/** The fields a form adds for the email box. Only a ticked box sends any. */
export function emailConsentPayload(
  optedIn: boolean,
): { emailOptIn: true; emailConsentVersion: string } | Record<string, never> {
  return optedIn ? { emailOptIn: true, emailConsentVersion: EMAIL_CONSENT_VERSION } : {};
}

/** True only when the request carries the ticked email box. */
export function hasExplicitEmailConsent(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as { emailOptIn?: unknown }).emailOptIn === true;
}
